import type { ReactNode } from 'react'
import { Popover } from 'radix-ui'
import { CircleHelp } from 'lucide-react'

/**
 * Contextual help next to a field label (E-UX) — for statutory and other non-obvious fields.
 * A Popover, not a hover-only tooltip, so it works on touch screens and with the keyboard.
 */
export function HelpTip({ children, label = 'More about this field' }: { children: ReactNode; label?: string }) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          className="ml-1 inline-grid size-4 translate-y-[2px] place-items-center rounded-full align-baseline text-muted-foreground hover:text-foreground print:hidden"
        >
          <CircleHelp className="size-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="top" align="start" sideOffset={6} collisionPadding={12}
          className="z-[100] max-w-[min(320px,calc(100vw-24px))] rounded-md border border-border bg-popover p-3 text-xs leading-relaxed text-popover-foreground shadow-e3 data-[state=open]:animate-in data-[state=open]:fade-in-0">
          {children}
          <Popover.Arrow className="fill-popover" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/** Plain-language help for statutory fields, keyed by field name — reused by every form. */
export const STATUTORY_HELP: Record<string, string> = {
  pan_number: 'Permanent Account Number from the Income Tax Department: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F). Needed for TDS; without it tax is deducted at the higher rate (sec. 206AA).',
  pan: 'Permanent Account Number: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F).',
  tan: 'Tax Deduction Account Number of the employer: 4 letters, 5 digits, 1 letter. Printed on Form 16 and every TDS return.',
  gstin: 'Goods and Services Tax Identification Number: 15 characters, starting with the 2-digit state code.',
  aadhaar_number: '12-digit Aadhaar. Stored encrypted and shown masked (last 4 digits only).',
  uan_number: 'Universal Account Number for EPF: 12 digits, the same across every employer. Ask the employee or check the EPFO member portal.',
  uan: 'Universal Account Number for EPF: 12 digits, the same across every employer.',
  esi_number: 'ESI insurance number (IP number), 10 digits. Only for employees whose gross wages are ₹21,000 a month or less.',
  pf_number: 'Member ID within this establishment\'s EPF account (region/office/establishment/extension/account).',
  epf_code: 'EPF establishment code allotted by EPFO, e.g. APHYD0012345000.',
  esi_code: 'ESI employer code (17 digits) allotted by ESIC.',
  pt_registration: 'Professional Tax registration (enrolment / certificate number) issued by the state.',
  bank_ifsc: 'Indian Financial System Code of the branch: 11 characters (4 letters, 0, then 6 letters or digits). Printed on the cheque book. Bank and branch fill in automatically.',
  ifsc_code: 'Indian Financial System Code of the branch: 11 characters (4 letters, 0, then 6 letters or digits). Bank and branch fill in automatically.',
  ifsc: 'Indian Financial System Code of the branch: 11 characters. Bank and branch fill in automatically.',
  current_pincode: '6-digit PIN code. The state is suggested from the first digits.',
  permanent_pincode: '6-digit PIN code. The state is suggested from the first digits.',
  pincode: '6-digit PIN code.',
  financial_year_start: 'Month the financial year starts. In India this is April for income tax.',
  payroll_process_day: 'Day of the month payroll is usually processed.',
}
